import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getMovieBannerInfo,
  getTVBannerInfo,
  getHomeMovies,
  getHomeTVs,
  getMoviesGenresList,
  getTVGenresList,
} from "../services/home";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useAppSelector } from "../store/hooks";
import PageTitle from "../components/Common/PageTitle";
import MobileNav from "../components/Common/MobileNav";
import Sidebar from "../components/Common/Sidebar";
import SearchBox from "../components/Common/SearchBox";
import RecommendGenres from "../components/Home/RecommendGenres";
import TrendingNow from "../components/Home/TrendingNow";
import MainHomeFilms from "../components/Home/MainHomeFilm";
import Footer from "../components/Footer/Footer";

const Home = () => {
  const currentUser = useAppSelector((state) => state.auth.user);

  const [currentTab, setCurrentTab] = useState(
    localStorage.getItem("currentTab") || "movie"
  );

  const [isSidebarActive, setIsSidebarActive] = useState(false);

  const {
    data: dataMovie,
    isLoading: isLoadingMovie,
    isError: isErrorMovie,
    error: errorMovie,
  } = useQuery(["home-movies"], getHomeMovies);

  const {
    data: dataMovieDetail,
    isLoading: isLoadingMovieDetail,
    isError: isErrorMovieDetail,
    error: errorMovieDetail,
  } = useQuery(
    ["detailMovies", dataMovie?.Trending],
    () => getMovieBannerInfo(dataMovie?.Trending),
    { enabled: !!dataMovie?.Trending }
  );

  const {
    data: dataTV,
    isLoading: isLoadingTV,
    isError: isErrorTV,
    error: errorTV,
  } = useQuery(["home-tvs"], getHomeTVs);

  const {
    data: dataTVDetail,
    isLoading: isLoadingTVDetail,
    isError: isErrorTVDetail,
    error: errorTVDetail,
  } = useQuery(
    ["detailTvs", dataTV?.Trending],
    () => getTVBannerInfo(dataTV?.Trending),
    { enabled: !!dataTV?.Trending }
  );

  const {
    data: moviesGenres,
    isLoading: isLoadingMoviesGenres,
    isError: isErrorMoviesGenres,
    error: errorMoviesGenres,
  } = useQuery(["movies-genres"], getMoviesGenresList);

  const {
    data: tvGenres,
    isLoading: isLoadingTVGenres,
    isError: isErrorTVGenres,
    error: errorTVGenres,
  } = useQuery(["tv-genres"], getTVGenresList);

  // MOVIE

  if (isErrorMovie) return <p>ERROR: {errorMovie.message}</p>;

  if (isLoadingMovie) return <p>Loading...</p>;

  if (isErrorMovieDetail) return <p>ERROR: {errorMovieDetail.message}</p>;

  if (isLoadingMovieDetail) return <p>Loading...</p>;

  // TV

  if (isErrorTV) return <p>ERROR: {errorTV.message}</p>;

  if (isLoadingTV) return <p>Loading...</p>;

  if (isErrorTVDetail) return <p>ERROR: {errorTVDetail.message}</p>;

  if (isLoadingTVDetail) return <p>Loading...</p>;

  // GENRES

  if (isErrorMoviesGenres) return <p>ERROR: {errorMoviesGenres.message}</p>;

  if (isLoadingMoviesGenres) return <p>Loading...</p>;

  if (isErrorTVGenres) return <p>ERROR: {errorTVGenres.message}</p>;

  if (isLoadingTVGenres) return <p>Loading...</p>;

  return (
    <>
      <PageTitle value="SHWETFLIX | Its Show Time" />
      <MobileNav setIsSidebarActive={setIsSidebarActive} />
      <div className="flex">
        <Sidebar
          setIsSidebarActive={setIsSidebarActive}
          isSidebarActive={isSidebarActive}
        />
        <div className="flex-grow py-7 md:pt-7 px-[3vw] md:px-[2vw] border-x border-gray-darken min-h-screen">
          <div className="flex justify-between items-center mb-5">
            <div className="inline-flex md:gap-3 relative">
              <button
                onClick={() => {
                  setCurrentTab("movie");
                  localStorage.setItem("currentTab", "movie");
                }}
                className={`px-3 py-2 w-[95px] rounded-full outline-0 border-0 mr-2 text-sm text-gray-400 
                  transition duration-300 hover:text-white
                 ${currentTab === "movie" && "!text-white tw-shaded-button"}`}
              >
                Movie
              </button>
              <button
                onClick={() => {
                  setCurrentTab("tv");
                  localStorage.setItem("currentTab", "tv");
                }}
                className={`px-4 py-2 w-[95px] rounded-full outline-0 border-0 mr-2 text-sm text-gray-400 
                transition duration-300 hover:text-white
                ${currentTab === "tv" && "!text-white tw-shaded-button"} `}
              >
                TV Show
              </button>
            </div>
            <div className="flex gap-2 md:gap-6 items-center">
              <p>{currentUser?.displayName || "Guest"}</p>
              <LazyLoadImage
                src={currentUser ? currentUser.photoURL : "/defaultAvatar.png"}
                alt="User avatar"
                className="w-8 h-8 rounded-full object-cover"
                effect="opacity"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>

          {currentTab === "movie" && (
            <MainHomeFilms
              data={dataMovie}
              dataDetail={dataMovieDetail}
              genresList={moviesGenres}
              isLoadingBanner={isLoadingMovieDetail}
              isLoadingSection={isLoadingMovie}
            />
          )}
          {currentTab === "tv" && (
            <MainHomeFilms
              data={dataTV}
              dataDetail={dataTVDetail}
              genresList={tvGenres}
              isLoadingBanner={isLoadingTVDetail}
              isLoadingSection={isLoadingTV}
            />
          )}
        </div>

        <div className="shrink-0 max-w-[310px] w-full hidden lg:block px-6 top-0 sticky ">
          <SearchBox />
          <RecommendGenres
            currentTab={currentTab}
            genresList={currentTab === "movie" ? moviesGenres : tvGenres}
          />
          <TrendingNow />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Home;
